import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import axiosInstance from 'src/utils/axios';
import { QUERY_KEY } from 'src/utils/queryKeys';

import { listenForegroundMessage, requestFcmToken, setupMessaging } from 'src/firebase';

import { toasterClasses } from 'src/components/snackbar/classes';

import { ButtonIcon } from 'src/sections/overview/courseManage/_components/styled';

import { getDeviceType } from './getDeviceType';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

function showClickableToast(title: string, body: string, onClick?: () => void) {
  toast.custom(
    (id) => (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          onClick?.();
          toast.dismiss(id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick?.();
            toast.dismiss(id);
          }
        }}
        className={`${toasterClasses.toast}`}
        style={{
          cursor: 'pointer',
        }}
      >
        <div
          className={toasterClasses.content}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'start',
            gap: 12,
          }}
        >
          <div>
            <p className={toasterClasses.title}>{title}</p>
            <div
              className={toasterClasses.description}
              dangerouslySetInnerHTML={{ __html: body }}
            />
          </div>

          <ButtonIcon
            aria-label="Đóng"
            onClick={(e) => {
              e.stopPropagation();
              toast.dismiss(id);
            }}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'rgba(255,255,255,.6)',
              fontSize: 16,
              lineHeight: 1,
              padding: 4,
              cursor: 'pointer',
            }}
          >
            ✕
          </ButtonIcon>
        </div>
      </div>
    ),
    {
      position: 'bottom-right',
    }
  );
}
export function useFcm(userId?: string) {
  const [token, setToken] = useState<string | null>(null);
  const [lastMsg, setLastMsg] = useState<any>(null);
  const mounted = useRef(true);
  const lastMsgIdRef = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    mounted.current = true;
    const unsubs: Array<() => void> = [];

    const initFcm = async () => {
      try {
        if (!('Notification' in window)) return;
        const permission =
          Notification.permission === 'default'
            ? await Notification.requestPermission()
            : Notification.permission;

        if (permission !== 'granted') return;

        if (!VAPID_PUBLIC_KEY) {
          console.warn('[FCM] Missing VAPID public key');
          return;
        }

        const messaging = await setupMessaging();
        if (!messaging) return;

        const t = await requestFcmToken(messaging, VAPID_PUBLIC_KEY);
        if (mounted.current) setToken(t);

        if (t && userId) {
          const device_type = getDeviceType();
          try {
            await axiosInstance.post('/notifications/register-token', {
              notification_token: t,
              device_type,
            });
          } catch (e) {
            console.error('[FCM] register-token failed:', e);
          }
        }

        const stop = listenForegroundMessage(messaging, (payload) => {
          const messageId = payload?.data?.messageId as string | undefined;
          if (messageId && lastMsgIdRef.current === messageId) return;
          lastMsgIdRef.current = messageId;

          const conversationId =
            (payload?.data?.conversationId as string | undefined) ??
            (payload?.fcmOptions?.link
              ? new URL(payload.fcmOptions.link).searchParams.get('id') || undefined
              : undefined);
          if (conversationId) {
            queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
          }
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY.MESSAGE.CONVERSATIONS] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY.MESSAGE.CONVERSATION] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY.NOTIFICATION.MY_NOTIFICATIONS] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY.NOTIFICATION.ADMIN] });

          setLastMsg(payload);
          const title = payload.notification?.title || 'Thông báo';
          const body = payload.notification?.body || '';
          showClickableToast(title, body, () => window.location.assign(payload.fcmOptions.link));
        });

        if (stop) unsubs.push(stop);
      } catch (e) {
        console.error('FCM init error:', e);
      }
    };

    initFcm();

    return () => {
      mounted.current = false;
      unsubs.forEach((u) => {
        try {
          u();
        } catch {
          /* empty */
        }
      });
    };
  }, [navigate, queryClient, userId]);

  return { token, lastMsg };
}
